/**
 * Typed registry of HTTP routes between the clients and the Firebase services.
 *
 * Today the client does `productApi.post("/add-product", payload)` against a bare
 * `AxiosInstance`: the path is a string, the body is `any`, the response is `any`.
 * Rename a route or change a body shape on the service side and nothing fails
 * until a user hits it (TRE-1684).
 *
 * This is the type layer only — no runtime behaviour changes. The services still
 * mount their express routes by hand; generating the routing from this registry
 * is TRE-1685. What it buys now: a mismatch between a declaration and a call site
 * fails `pnpm type-check`.
 *
 * Everything here must stay types plus small plain objects. `data-models` is
 * imported by the mobile app, so nothing heavy may leak in.
 */

export type HttpMethod = "get" | "post" | "put" | "patch" | "delete";

/**
 * How a route is protected. Recorded now, enforced by `createHttpFunction` in
 * TRE-1685.
 *
 * - `public` — no auth (webhooks, payment notifications)
 * - `optional` — decodes a token when present (`includeDecodedTokenIfExists`)
 * - `user` — a signed-in user (`ensureAuthenticated`)
 * - `supabase` — a Supabase service token (`ensureSupabaseAuthenticated`)
 * - `admin` — the main trainer (`ensureAdminAuthenticated`)
 * - `permission` — the owner OR a staff member holding the route's permission
 *   keys (`ensurePermissionAuthenticated` / a `products.manage`-style guard).
 *   The keys themselves stay with the service that mounts the route; the level
 *   only records that the gate is a permission, not the owner's uid — so a
 *   route migrated to `mountRoutes` is not silently re-locked to owner-only.
 * - `task` — a Cloud Tasks / shared-secret callback
 */
export type AuthLevel = "public" | "optional" | "user" | "supabase" | "admin" | "permission" | "task";

/** What a route is, with no payload types attached — the constraint to accept. */
export interface RouteMeta {
  readonly method: HttpMethod;
  readonly path: string;
  readonly auth: AuthLevel;
}

export interface RouteDefinition<TBody = void, TResponse = void, TParams = void, TQuery = void> extends RouteMeta {
  /** Phantom carriers — never present at runtime. */
  readonly __body?: TBody;
  readonly __response?: TResponse;
  readonly __params?: TParams;
  readonly __query?: TQuery;
}

/**
 * Declare a route and the shapes that travel over it.
 *
 * ```ts
 * addProduct: typed<CreateProductDTO, void>()({ method: "post", path: "/add-product", auth: "user" }),
 * ```
 */
export const typed =
  <TBody = void, TResponse = void, TParams = void, TQuery = void>() =>
  <const TRoute extends { method: HttpMethod; path: string; auth: AuthLevel }>(
    route: TRoute,
  ): TRoute & RouteDefinition<TBody, TResponse, TParams, TQuery> =>
    route as TRoute & RouteDefinition<TBody, TResponse, TParams, TQuery>;

export interface CloudFunctionDefinition<TRoutes extends Record<string, RouteMeta>> {
  /** Deployed function name, e.g. `products-api`. */
  readonly functionName: string;
  readonly region: string;
  /** Version prefix of the surface, so v1 and v2 can coexist (TRE-1685). */
  readonly version: `v${number}`;
  readonly routes: TRoutes;
}

/** Identity with inference — keeps the declarations readable and `as const`-free. */
export const createCloudFunctions = <
  const T extends Record<string, CloudFunctionDefinition<Record<string, RouteMeta>>>,
>(
  definitions: T,
): T => definitions;

export type RouteBody<TRoute> = TRoute extends { __body?: infer TBody } ? Exclude<TBody, undefined> : void;
export type RouteResponse<TRoute> = TRoute extends { __response?: infer TResponse }
  ? Exclude<TResponse, undefined>
  : void;
export type RouteParams<TRoute> = TRoute extends { __params?: infer TParams } ? Exclude<TParams, undefined> : void;
export type RouteQuery<TRoute> = TRoute extends { __query?: infer TQuery } ? Exclude<TQuery, undefined> : void;

export interface FunctionUrlOptions {
  projectId: string;
  /** Set when running against the Firebase emulator suite. */
  emulator?: { host: string; port: number };
}

/** `https://<region>-<projectId>.cloudfunctions.net/<functionName>`, or the emulator equivalent. */
export const buildFunctionUrl = (
  fn: Pick<CloudFunctionDefinition<Record<string, RouteMeta>>, "functionName" | "region">,
  { projectId, emulator }: FunctionUrlOptions,
): string =>
  emulator
    ? `http://${emulator.host}:${emulator.port}/${projectId}/${fn.region}/${fn.functionName}`
    : `https://${fn.region}-${projectId}.cloudfunctions.net/${fn.functionName}`;

/** Fills `:param` placeholders — `/tpay-notification/:productId/:orderId`. */
export const buildRoutePath = (route: Pick<RouteMeta, "path">, params: Record<string, string | number> = {}) =>
  route.path.replace(/:([A-Za-z0-9_]+)/g, (match, name: string) => {
    const value = params[name];
    if (value === undefined) throw new Error(`Missing route parameter "${name}" for ${route.path}`);
    return String(value);
  });
